import {DevFeature} from './types';

/*
 * FEATURES
 * =====================================================
 */

// Feature: Service Request Addresses (Appointment Details, Action Queue scheduling, Send to MCI)
const SERVICE_REQUEST_ADDRESSES_FEATURE_ID = 'serviceRequestAddresses';
enum ServiceRequestAddressesScenario {
    Two = 'two',
    Three = 'three',
    One = 'one',
    TwoWithTbd = 'twoWithTbd',
    ThreeWithTbd = 'threeWithTbd',
    None = 'none',
}
const serviceRequestAddressesFeature: DevFeature = {
    id: SERVICE_REQUEST_ADDRESSES_FEATURE_ID,
    title: 'Service Request Addresses',
    description:
        'Service Request addresses shown across Appointment Details, the Action Queue scheduling detail, the Scheduling Service Request screen and the Edit Scheduled Service Request modal. TBD variants enable the "Edit Location" button.',
    origin: 'CESIA-514 · Epic MARK-4093',
    relatedTickets: ['CESIA-275', 'CESIA-380', 'CESIA-402', 'CESIA-514'],
    defaultScenarioId: ServiceRequestAddressesScenario.Two,
    scenarios: [
        {id: ServiceRequestAddressesScenario.Two, label: '2 addresses (from + to)'},
        {id: ServiceRequestAddressesScenario.Three, label: '3 addresses'},
        {id: ServiceRequestAddressesScenario.One, label: '1 address'},
        {id: ServiceRequestAddressesScenario.TwoWithTbd, label: '2 addresses (2nd is TBD)'},
        {id: ServiceRequestAddressesScenario.ThreeWithTbd, label: '3 addresses (2nd is TBD)'},
        {id: ServiceRequestAddressesScenario.None, label: 'No addresses'},
    ],
};

// Feature: Service Request Address Update outcome
const SERVICE_REQUEST_ADDRESS_UPDATE_FEATURE_ID = 'serviceRequestAddressUpdate';
enum ServiceRequestAddressUpdateScenario {
    Success = 'success',
    DuplicateAddresses = 'duplicateAddresses',
    MissingAddresses = 'missingAddresses',
    NotModifiable = 'notModifiable',
    SentToMci = 'sentToMci',
    ServerError = 'serverError',
}
const serviceRequestAddressUpdateFeature: DevFeature = {
    id: SERVICE_REQUEST_ADDRESS_UPDATE_FEATURE_ID,
    title: 'Service Request Address Update',
    description:
        'Outcome of saving a multi-stop route — from the "Edit Location" modal on the Scheduling Service Request screen and from the Edit Scheduled Service Request modal. On success the saved route is kept in memory, so the next read reflects the change.',
    origin: 'CESIA-380 · CESIA-402',
    relatedTickets: ['CESIA-380', 'CESIA-402'],
    defaultScenarioId: ServiceRequestAddressUpdateScenario.Success,
    scenarios: [
        {id: ServiceRequestAddressUpdateScenario.Success, label: 'Saves successfully'},
        {id: ServiceRequestAddressUpdateScenario.DuplicateAddresses, label: '400 — duplicate addresses'},
        {id: ServiceRequestAddressUpdateScenario.MissingAddresses, label: '400 — both addresses required'},
        {id: ServiceRequestAddressUpdateScenario.NotModifiable, label: '422 — status not modifiable'},
        {id: ServiceRequestAddressUpdateScenario.SentToMci, label: '422 — already sent to MCI'},
        {id: ServiceRequestAddressUpdateScenario.ServerError, label: '500 — server error'},
    ],
};

// Feature: Logistics Engine Cancellation Indicator
const LOGISTICS_ENGINE_CANCELLATION_FEATURE_ID = 'logisticsEngineCancellation';
enum LogisticsEngineCancellationScenario {
    Canceled = 'canceled',
    NotCanceled = 'notCanceled',
}
const logisticsEngineCancellationFeature: DevFeature = {
    id: LOGISTICS_ENGINE_CANCELLATION_FEATURE_ID,
    title: 'Logistics Engine Cancellation Indicator',
    description:
        'Indicator in the Scheduling Service Request View when a request was sent to the logistics engine and canceled there (most recent cancellation time).',
    origin: 'CESIA-460',
    relatedTickets: ['CESIA-457', 'CESIA-458', 'CESIA-460'],
    defaultScenarioId: LogisticsEngineCancellationScenario.NotCanceled,
    scenarios: [
        {id: LogisticsEngineCancellationScenario.Canceled, label: 'Canceled by logistics engine'},
        {id: LogisticsEngineCancellationScenario.NotCanceled, label: 'Not canceled'},
    ],
};

// Feature: Manual Send to Logistics Engine action
const SEND_TO_LOGISTIC_ENGINE_FEATURE_ID = 'sendToLogisticEngine';
enum SendToLogisticEngineScenario {
    Eligible = 'eligible',
    Pending = 'pending',
    Declined = 'declined',
    SendFailure = 'sendFailure',
}
const sendToLogisticEngineFeature: DevFeature = {
    id: SEND_TO_LOGISTIC_ENGINE_FEATURE_ID,
    title: 'Send to Logistics Engine Action',
    description:
        'Manual "Send to Logistics Engine" button on an eligible vendor in the Scheduling Service Request View, and the pending state after it is sent.',
    origin: 'CESIA-462',
    relatedTickets: ['CESIA-387', 'CESIA-515'],
    defaultScenarioId: SendToLogisticEngineScenario.Eligible,
    scenarios: [
        {id: SendToLogisticEngineScenario.Eligible, label: 'Eligible (button shown)'},
        {id: SendToLogisticEngineScenario.Pending, label: 'Sent · pending in logistics engine (button hidden)'},
        {id: SendToLogisticEngineScenario.Declined, label: 'Sent · declined by logistics engine'},
        {id: SendToLogisticEngineScenario.SendFailure, label: 'Eligible · send fails (500)'},
    ],
};

// Feature: Scheduler-Calculated Arrival Window
const ARRIVAL_WINDOW_FEATURE_ID = 'arrivalWindow';
enum ArrivalWindowScenario {
    SameDay = 'sameDay',
    NextDay = 'nextDay',
    Absent = 'absent',
}
const arrivalWindowFeature: DevFeature = {
    id: ARRIVAL_WINDOW_FEATURE_ID,
    title: 'Scheduler-Calculated Arrival Window',
    description:
        'Backend-provided arrivalWindowStart/arrivalWindowEnd (patient-local) shown as-is on appointment tables and overviews. In Home is backend-only, so absent fields render TBD.',
    origin: 'CESIA-595 · CESIA-596',
    relatedTickets: ['CESIA-595', 'CESIA-596'],
    defaultScenarioId: ArrivalWindowScenario.Absent,
    scenarios: [
        {id: ArrivalWindowScenario.SameDay, label: 'Backend window (same day)'},
        {id: ArrivalWindowScenario.NextDay, label: 'Backend window (crosses midnight)'},
        {id: ArrivalWindowScenario.Absent, label: 'No backend window (renders TBD)'},
    ],
};

// Feature: Cancel Appointment Vendor Integration Message
const CANCEL_APPOINTMENT_INTEGRATION_FEATURE_ID = 'cancelAppointmentIntegration';
enum CancelAppointmentIntegrationScenario {
    NonIntegrated = 'nonIntegrated',
    Atlas = 'atlas',
    Ccc = 'ccc',
    ServiceConnect = 'serviceConnect',
}
const cancelAppointmentIntegrationFeature: DevFeature = {
    id: CANCEL_APPOINTMENT_INTEGRATION_FEATURE_ID,
    title: 'Cancel Appointment · Vendor Integration Message',
    description:
        'Edit Appointment → Cancel Appointment modal message driven by the performer vendor integration: the service-coordinator question (non-integrated), "canceled via Logistics Engine" for Atlas (needs LV1Integration flag), "canceled via CCC" for Service Now (needs CesiaCCCIntegration flag), or "canceled via Service Connect" for Service Connect.',
    origin: 'CESIA-698',
    relatedTickets: ['CESIA-688', 'CESIA-698', 'CESIA-727'],
    defaultScenarioId: CancelAppointmentIntegrationScenario.NonIntegrated,
    scenarios: [
        {
            id: CancelAppointmentIntegrationScenario.NonIntegrated,
            label: 'Non-integrated · service coordinator question',
        },
        {
            id: CancelAppointmentIntegrationScenario.Atlas,
            label: 'Atlas · canceled via Logistics Engine (needs LV1 flag)',
        },
        {
            id: CancelAppointmentIntegrationScenario.Ccc,
            label: 'Service Now · canceled via CCC (needs CCC flag)',
        },
        {
            id: CancelAppointmentIntegrationScenario.ServiceConnect,
            label: 'Service Connect · canceled via Service Connect',
        },
    ],
};

// Feature: Change Partner · Available Partners integration CTA
const CHANGE_PARTNER_AVAILABLE_PARTNERS_FEATURE_ID = 'changePartnerAvailablePartners';
enum ChangePartnerAvailablePartnersScenario {
    MciPartner = 'changePartnerMci',
    ServiceConnectPartner = 'changePartnerServiceConnect',
    AtlasPartner = 'changePartnerAtlas',
    NonIntegratedPartner = 'changePartnerNonIntegrated',
    MixedPartners = 'changePartnerMixed',
    NoPartners = 'changePartnerNone',
}
const changePartnerAvailablePartnersFeature: DevFeature = {
    id: CHANGE_PARTNER_AVAILABLE_PARTNERS_FEATURE_ID,
    title: 'Change Partner · Available Partners',
    description:
        'Edit Appointment → Change Partner → "Change Myself": Available Partners rows. MCI Integration-enabled sites show "Partner Uses Direct Integration" + the cancel-and-send-to-action-queue CTA (needs CesiaMciIntegration flag); Service Connect / Logistics Engine show their own CTA; others show "Select Partner". Confirming the modal cancels the appointment.',
    origin: 'CESIA-510',
    relatedTickets: ['CESIA-223', 'CESIA-510'],
    defaultScenarioId: ChangePartnerAvailablePartnersScenario.MixedPartners,
    scenarios: [
        {id: ChangePartnerAvailablePartnersScenario.MciPartner, label: 'MCI Integration partner (needs MCI flag)'},
        {id: ChangePartnerAvailablePartnersScenario.ServiceConnectPartner, label: 'Service Connect partner'},
        {id: ChangePartnerAvailablePartnersScenario.AtlasPartner, label: 'Logistics Engine partner'},
        {id: ChangePartnerAvailablePartnersScenario.NonIntegratedPartner, label: 'Non-integrated partner'},
        {id: ChangePartnerAvailablePartnersScenario.MixedPartners, label: 'Mixed · MCI + SC + LE + non-integrated'},
        {id: ChangePartnerAvailablePartnersScenario.NoPartners, label: 'No available partners'},
    ],
};

// Feature: Cradlepoint Connectivity Status
const CRADLEPOINT_STATUS_FEATURE_ID = 'cradlepointStatus';
enum CradlepointStatusScenario {
    EthernetWithBackup = 'ethernetWithBackup',
    WifiWithBackup = 'wifiWithBackup',
    EthernetNoBackup = 'ethernetNoBackup',
    UnknownServiceSim = 'unknownServiceSim',
    SimOnly = 'simOnly',
    NoActiveInterface = 'noActiveInterface',
    RouterOffline = 'routerOffline',
    EmptyInterfaces = 'emptyInterfaces',
    EmptyObject = 'emptyObject',
    NullBody = 'nullBody',
    NoContent = 'noContent',
    Loading = 'loading',
    NotFound = 'notFound',
    ServerError = 'serverError',
}
const cradlepointStatusFeature: DevFeature = {
    id: CRADLEPOINT_STATUS_FEATURE_ID,
    title: 'Cradlepoint Connectivity Status',
    description:
        'Connectivity badge in the patient header — Strong / Poor / No connection with the video tooltip, driven by the Cradlepoint router status endpoint. The badge renders nothing when the request fails.',
    origin: 'Cradlepoint router status',
    relatedTickets: [],
    defaultScenarioId: CradlepointStatusScenario.EthernetWithBackup,
    scenarios: [
        {id: CradlepointStatusScenario.EthernetWithBackup, label: 'Strong · Ethernet + 2 SIM backups'},
        {id: CradlepointStatusScenario.WifiWithBackup, label: 'Strong · WiFi + SIM backup'},
        {id: CradlepointStatusScenario.EthernetNoBackup, label: 'Poor · Ethernet, no backup'},
        {id: CradlepointStatusScenario.UnknownServiceSim, label: 'Poor · Ethernet + "Unknown Service" SIM'},
        {id: CradlepointStatusScenario.SimOnly, label: 'Poor · SIM1 only (no Ethernet/WiFi)'},
        {id: CradlepointStatusScenario.NoActiveInterface, label: 'No connection · online, nothing connected'},
        {id: CradlepointStatusScenario.RouterOffline, label: 'No connection · router offline'},
        {id: CradlepointStatusScenario.EmptyInterfaces, label: 'Empty · router with no interfaces at all'},
        {id: CradlepointStatusScenario.EmptyObject, label: 'Empty · 200 with {} (no interfaces → badge hidden)'},
        {id: CradlepointStatusScenario.NullBody, label: 'Empty · 200 with null body (badge hidden)'},
        {id: CradlepointStatusScenario.NoContent, label: 'Empty · 204 No Content (badge hidden)'},
        {id: CradlepointStatusScenario.Loading, label: 'Loading · request never resolves (spinner)'},
        {id: CradlepointStatusScenario.NotFound, label: '404 — device not found (badge hidden)'},
        {id: CradlepointStatusScenario.ServerError, label: '500 — server error (badge hidden)'},
    ],
};

// Feature: Patient Documents
const PATIENT_DOCUMENTS_FEATURE_ID = 'patientDocuments';
enum PatientDocumentsScenario {
    Three = 'three',
    Empty = 'empty',
    One = 'one',
}
const patientDocumentsFeature: DevFeature = {
    id: PATIENT_DOCUMENTS_FEATURE_ID,
    title: 'Patient Documents',
    description: 'Patient Profile document list and interactive upload/delete flow, including the empty state.',
    origin: 'Patient Profile',
    relatedTickets: [],
    defaultScenarioId: PatientDocumentsScenario.Three,
    scenarios: [
        {id: PatientDocumentsScenario.Three, label: '3 documents'},
        {id: PatientDocumentsScenario.Empty, label: 'No documents'},
        {id: PatientDocumentsScenario.One, label: '1 document'},
    ],
};

// Aggregated map of every scenario enum, reused wherever a scenario id is needed.
const allScenarios = {
    ...ServiceRequestAddressesScenario,
    ...ServiceRequestAddressUpdateScenario,
    ...LogisticsEngineCancellationScenario,
    ...SendToLogisticEngineScenario,
    ...ArrivalWindowScenario,
    ...CancelAppointmentIntegrationScenario,
    ...ChangePartnerAvailablePartnersScenario,
    ...CradlepointStatusScenario,
    ...PatientDocumentsScenario,
};

const devFeatures: DevFeature[] = [
    serviceRequestAddressesFeature,
    serviceRequestAddressUpdateFeature,
    logisticsEngineCancellationFeature,
    sendToLogisticEngineFeature,
    arrivalWindowFeature,
    cancelAppointmentIntegrationFeature,
    changePartnerAvailablePartnersFeature,
    cradlepointStatusFeature,
    patientDocumentsFeature,
];

export {
    allScenarios,
    ARRIVAL_WINDOW_FEATURE_ID,
    ArrivalWindowScenario,
    CANCEL_APPOINTMENT_INTEGRATION_FEATURE_ID,
    CancelAppointmentIntegrationScenario,
    CHANGE_PARTNER_AVAILABLE_PARTNERS_FEATURE_ID,
    ChangePartnerAvailablePartnersScenario,
    CRADLEPOINT_STATUS_FEATURE_ID,
    CradlepointStatusScenario,
    devFeatures,
    LOGISTICS_ENGINE_CANCELLATION_FEATURE_ID,
    LogisticsEngineCancellationScenario,
    PATIENT_DOCUMENTS_FEATURE_ID,
    PatientDocumentsScenario,
    SEND_TO_LOGISTIC_ENGINE_FEATURE_ID,
    SendToLogisticEngineScenario,
    SERVICE_REQUEST_ADDRESS_UPDATE_FEATURE_ID,
    SERVICE_REQUEST_ADDRESSES_FEATURE_ID,
    ServiceRequestAddressesScenario,
    ServiceRequestAddressUpdateScenario,
};
